import { createSlice, PayloadAction, UnknownAction } from '@reduxjs/toolkit';

import {
  TMergeResourceTagsPayload,
  TMergeReadableResourcesPayload,
  TPermissionsState,
} from '@/shared/features/permissions/store/permissions.types';

const initialState: TPermissionsState = { resources: {} };

// Matched by type string (not by importing the thunk) to avoid a circular import
// through the auth slice during store initialization.
const LOGOUT_FULFILLED = 'auth/logout/fulfilled';

// Progressively harvested readable resources (see permissions.listener). The
// App permission providers read this to build the permission model — id/group/tag
// scopes resolve as the app fetches the lists that contain those resources.
const permissionsSlice = createSlice({
  name: 'permissions',
  initialState,
  reducers: {
    mergeReadableResources: (
      state,
      { payload }: PayloadAction<TMergeReadableResourcesPayload>,
    ) => {
      const bucket = state.resources[payload.entity] ?? {};

      payload.items.forEach(({ id, groupId }) => {
        bucket[id] = { ...bucket[id], groupId: groupId ?? bucket[id]?.groupId };
      });

      state.resources[payload.entity] = bucket;
    },
    mergeResourceTags: (
      state,
      { payload }: PayloadAction<TMergeResourceTagsPayload>,
    ) => {
      const bucket = state.resources[payload.entity] ?? {};

      payload.items.forEach(({ id, tags }) => {
        bucket[id] = { ...bucket[id], tags: tags ?? bucket[id]?.tags };
      });

      state.resources[payload.entity] = bucket;
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      (action: UnknownAction) => action.type === LOGOUT_FULFILLED,
      () => initialState,
    );
  },
});

export const { mergeReadableResources, mergeResourceTags } =
  permissionsSlice.actions;

export const selectPermissionResources = (state: {
  permissions: TPermissionsState;
}) => state.permissions.resources;

export default permissionsSlice.reducer;
